import React, { useMemo } from 'react'
import type { CSSProperties } from 'react'
import type { PieChartProps, PieChartSlice } from './PieChart.types'
import {
  PIE_CHART_DEFAULT_SIZE,
  PIE_CHART_DEFAULT_SLICES,
} from './PieChart.types'
import './PieChart.css'

type PolarPoint = { x: number; y: number }

/** Angle 0 at 12 o’clock, increasing clockwise → SVG cartesian. */
const polarToCartesian = (
  cx: number,
  cy: number,
  radius: number,
  angleFromTopClockwise: number,
): PolarPoint => {
  const theta = -Math.PI / 2 + angleFromTopClockwise
  return {
    x: cx + radius * Math.cos(theta),
    y: cy + radius * Math.sin(theta),
  }
}

const describeSlicePath = (
  cx: number,
  cy: number,
  radius: number,
  startAngle: number,
  endAngle: number,
): string => {
  const start = polarToCartesian(cx, cy, radius, startAngle)
  const end = polarToCartesian(cx, cy, radius, endAngle)
  const sweep = endAngle - startAngle
  const largeArc = sweep > Math.PI ? 1 : 0

  if (sweep >= Math.PI * 2 - 1e-6) {
    const mid = polarToCartesian(cx, cy, radius, startAngle + Math.PI)
    return [
      `M ${cx} ${cy}`,
      `L ${start.x} ${start.y}`,
      `A ${radius} ${radius} 0 1 1 ${mid.x} ${mid.y}`,
      `A ${radius} ${radius} 0 1 1 ${start.x} ${start.y}`,
      'Z',
    ].join(' ')
  }

  return [
    `M ${cx} ${cy}`,
    `L ${start.x} ${start.y}`,
    `A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y}`,
    'Z',
  ].join(' ')
}

const normalizeSlices = (slices: PieChartSlice[]): PieChartSlice[] => {
  const positive = slices.filter((slice) => slice.value > 0)
  return positive.length > 0 ? positive : PIE_CHART_DEFAULT_SLICES
}

const PieChart: React.FC<PieChartProps> = ({
  slices = PIE_CHART_DEFAULT_SLICES,
  size = PIE_CHART_DEFAULT_SIZE,
  className = '',
  style,
  'aria-label': ariaLabel,
}) => {
  const resolvedSlices = useMemo(() => normalizeSlices(slices), [slices])

  const paths = useMemo(() => {
    const total = resolvedSlices.reduce((sum, slice) => sum + slice.value, 0)
    const cx = size / 2
    const cy = size / 2
    const radius = size / 2
    let angle = 0

    return resolvedSlices.map((slice, index) => {
      const sweep = (slice.value / total) * Math.PI * 2
      const startAngle = angle
      const endAngle = angle + sweep
      angle = endAngle

      return {
        key: `${slice.label ?? 'slice'}-${index}`,
        d: describeSlicePath(cx, cy, radius, startAngle, endAngle),
        color: slice.color,
        label: slice.label,
      }
    })
  }, [resolvedSlices, size])

  const classNames = ['ds-pie-chart', className].filter(Boolean).join(' ')

  const rootStyle = {
    ...style,
    '--ds-pie-chart-size': `${size}px`,
  } as CSSProperties

  return (
    <div
      className={classNames}
      style={rootStyle}
      role="img"
      aria-label={ariaLabel ?? 'Pie chart'}
    >
      <svg
        className="ds-pie-chart__svg"
        viewBox={`0 0 ${size} ${size}`}
        width={size}
        height={size}
        aria-hidden="true"
      >
        {paths.map((path) => (
          <path key={path.key} d={path.d} fill={path.color} stroke="none">
            {path.label ? <title>{path.label}</title> : null}
          </path>
        ))}
      </svg>
    </div>
  )
}

export default PieChart
